'use strict';

var internalAuthContext = require('../../../../../internal-auth-context-cu8DDA6h.js');
var _rollupPluginBabelHelpers = require('../../../../../_rollupPluginBabelHelpers-D-dvSLAU.js');
var React = require('react');
var modules_auth_hooks_useBaseAuthHelper_index = require('../../../hooks/use-base-auth-helper/index.js');
var modules_auth_internal_constants = require('../../constants.js');
var jsxRuntime = require('react/jsx-runtime');
require('../../../create-logged-in-fetch/index.js');

/** Decoded JWT token for the user. */

/**
 * A helper for managing internal authorization state. You should not
 *   use this one directly in product, instead opting to use the
 *   {@see InternalAuthProvider} and {@see useInternalAuth} hook.
 */
var useInternalAuthHelper = function useInternalAuthHelper(_ref) {
  var apiBaseUrl = _ref.apiBaseUrl,
    fetchFunction = _ref.fetchFunction;
  var _useBaseAuthHelper = modules_auth_hooks_useBaseAuthHelper_index.useBaseAuthHelper({
      hashParameterTokenKey: modules_auth_internal_constants.INTERNAL_AUTH_TOKEN_HASH_PARAMETER,
      localStorageTokenKey: modules_auth_internal_constants.INTERNAL_AUTH_TOKEN_LOCAL_KEY,
      fetchFunction: fetchFunction
    }),
    isLoggedIn = _useBaseAuthHelper.isLoggedIn,
    tokenInfo = _useBaseAuthHelper.tokenInfo,
    loggedInFetch = _useBaseAuthHelper.loggedInFetch,
    jwtToken = _useBaseAuthHelper.jwtToken,
    setJwtToken = _useBaseAuthHelper.setJwtToken;

  /**
   * Store the user's groups in a separate variable for easier access.
   */
  var userGroups = React.useMemo(function () {
    return (tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo['https://auth.ownup.com/groups']) || [];
  }, [tokenInfo]);

  /**
   * When the user has a valid token, store the email on the token as
   *   a login hint for future logins. This will allow them to skip
   *   the user selection screen (provided they still have access to
   *   that account.)
   */
  React.useEffect(function () {
    if (tokenInfo) {
      localStorage[modules_auth_internal_constants.INTERNAL_AUTH_LOGIN_HINT_KEY] = tokenInfo.email;
    }
  }, [tokenInfo]);

  /**
   * Immediately redirect the user to the login page. If the user has
   *   a login hint stored in their localStorage it will be sent along
   *   as part of the login request.
   *
   * @param redirectUrl - The page the user wants to go to after
   *   logging in. This _must_ be provided and must be a URL that
   *   is allowed by the server.
   */
  var login = React.useCallback(/*#__PURE__*/function () {
    var _ref2 = _rollupPluginBabelHelpers._asyncToGenerator(/*#__PURE__*/_rollupPluginBabelHelpers._regeneratorRuntime().mark(function _callee(redirectUrl) {
      var loginHint, queryStringParameters;
      return _rollupPluginBabelHelpers._regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            loginHint = localStorage[modules_auth_internal_constants.INTERNAL_AUTH_LOGIN_HINT_KEY];
            queryStringParameters = new URLSearchParams(_rollupPluginBabelHelpers._objectSpread2(_rollupPluginBabelHelpers._objectSpread2({}, redirectUrl ? {
              redirectUrl: redirectUrl
            } : {}), loginHint ? {
              loginHint: loginHint
            } : {})).toString();
            window.location.href = "".concat(apiBaseUrl, "/auth/login").concat(queryStringParameters ? "?".concat(queryStringParameters) : '');
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [apiBaseUrl]);

  /**
   * Immediately remove the user's authentication state and ask the server
   *   to log the user out. Will proceed as a logout even if the server
   *   fails to process the logout or takes too long (5s)
   *
   * @param redirectUrl - The page the user wants to go to after
   *   logging out.
   */
  var logout = React.useCallback(/*#__PURE__*/function () {
    var _ref3 = _rollupPluginBabelHelpers._asyncToGenerator(/*#__PURE__*/_rollupPluginBabelHelpers._regeneratorRuntime().mark(function _callee2(redirectUrl) {
      return _rollupPluginBabelHelpers._regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return Promise.race([loggedInFetch("".concat(apiBaseUrl, "/auth/logout"), {
              method: 'POST'
            })["catch"](function (err) {
              return console.error('Error logging out:', err);
            }), new Promise(function (res) {
              return setTimeout(res, 5000);
            })]);
          case 2:
            setJwtToken(undefined);
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x2) {
      return _ref3.apply(this, arguments);
    };
  }(), [apiBaseUrl, loggedInFetch, setJwtToken]);
  return {
    login: login,
    logout: logout,
    isLoggedIn: isLoggedIn,
    jwtToken: jwtToken,
    tokenInfo: tokenInfo,
    loggedInFetch: loggedInFetch,
    userGroups: userGroups
  };
};

/**
 * A context provider for {@see InternalAuthHelper}. Allows any
 *   child components to leverage our internal authorization
 *   tooling via the {@see useInternalAuth} hook.
 *
 * There should only be one of these providers in the application,
 *   as the helper class interacts with the browser's local storage
 *   and hash parameters and having multiple instances could lead
 *   to unexpected behavior.
 *
 * @param props - {@see InternalAuthProviderProps}
 */
var InternalAuthProvider = function InternalAuthProvider(_ref) {
  var apiBaseUrl = _ref.apiBaseUrl,
    children = _ref.children;
  var internalAuthHelper = useInternalAuthHelper({
    apiBaseUrl: apiBaseUrl
  });
  return jsxRuntime.jsx(internalAuthContext.InternalAuthContext.Provider, {
    value: internalAuthHelper,
    children: children
  });
};

exports.InternalAuthProvider = InternalAuthProvider;
//# sourceMappingURL=index.js.map
